<script setup lang="ts">
import type { IbexaFigureProps } from '~/utils/ibexa-bodytext'
import { faXmark } from '@fortawesome/pro-light-svg-icons'
import { DialogClose, DialogContent, DialogDescription, DialogOverlay, DialogPortal, DialogRoot, DialogTitle, DialogTrigger } from 'reka-ui'

const props = defineProps<IbexaFigureProps>()

const isOpen = ref<boolean>(false)
const dialogContentRef = ref<{ $el?: HTMLElement } | null>(null)
const scrollRegionRef = ref<HTMLElement | null>(null)
const lightboxImgRef = ref<HTMLImageElement | null>(null)

const initial = computed(() => props.byline?.[0]?.toUpperCase() ?? '')

const inlineCaptionId = `ibexa-fig-cap-inline-${useId()}`

const isInteractive = computed(() => !!props.alt || !!props.caption)
const hasModalCaption = computed(() => !!(props.caption || props.credit || props.byline))

const isZoomed = ref<boolean>(false)

// `aria-label` replaces image `alt` text, so include the figure name in the label
const zoomLabel = computed(() => {
  const action = isZoomed.value ? 'Zoom ut' : 'Zoom inn'
  const figureName = props.alt || props.caption
  return figureName ? `${action}: ${figureName}` : action
})

watch(isOpen, (open) => {
  if (!open) {
    isZoomed.value = false
  }
})

async function toggleZoom(event: MouseEvent | KeyboardEvent) {
  if (isZoomed.value) {
    isZoomed.value = false
    return
  }
  const img = lightboxImgRef.value
  const region = scrollRegionRef.value
  // No pointer coordinates for keyboard activation, use the image center
  const isPointer = event instanceof MouseEvent && event.detail > 0
  const fx = isPointer && img && img.clientWidth > 0 ? event.offsetX / img.clientWidth : 0.5
  const fy = isPointer && img && img.clientHeight > 0 ? event.offsetY / img.clientHeight : 0.5
  isZoomed.value = true
  await nextTick()
  if (img && region) {
    region.scrollLeft = computeAnchoredScroll(fx, img.clientWidth, region.clientWidth)
    region.scrollTop = computeAnchoredScroll(fy, img.clientHeight, region.clientHeight)
  }
}

function focusDialogContent(event: Event) {
  event.preventDefault()
  dialogContentRef.value?.$el?.focus()
}
</script>

<script lang="ts">
// Center the active image point, browser clamps scroll limits
export function computeAnchoredScroll(fraction: number, contentSize: number, viewportSize: number): number {
  return Math.max(0, fraction * contentSize - viewportSize / 2)
}
</script>

<template>
  <DialogRoot v-model:open="isOpen">
    <figure class="flex flex-col items-center max-w-full" data-marking-content :aria-label="props.caption || props.alt || undefined">
      <DialogTrigger v-if="isInteractive" as-child>
        <button
          type="button"
          class="cursor-pointer focus-visible:outline-2 focus-visible:outline-blurple-500 dark:focus-visible:outline-blurple-300"
          aria-label="Forstørr bildet"
          :aria-describedby="props.caption ? inlineCaptionId : undefined"
        >
          <img :src="props.src" :alt="props.alt" class="block rounded-2xl w-auto h-auto">
        </button>
      </DialogTrigger>
      <img v-else :src="props.src" :alt="props.alt" class="block rounded-2xl w-auto h-auto max-w-[min(100%,480px)] max-h-[70vh]">

      <figcaption v-if="props.caption || props.credit || props.byline" :id="inlineCaptionId" class="mt-xs w-full text-center font-body text-xs leading-5 sm:text-sm italic">
        <div v-if="props.caption || props.credit" class="flex items-center gap-xs flex-wrap">
          <span v-if="props.caption" class="text-primary not-italic">{{ props.caption }}</span>
          <span v-if="props.credit" class="text-carbon-400 dark:text-carbon-200 not-italic">© {{ props.credit }}</span>
        </div>
        <div v-if="props.byline" class="mt-2xs flex justify-center items-center gap-xs">
          <span class="inline-flex items-center justify-center w-6 h-6 rounded-full bg-moss-500 text-white text-xs font-medium">
            {{ initial }}
          </span>
          <span class="not-italic">{{ props.byline }}</span>
        </div>
      </figcaption>
    </figure>

    <DialogPortal>
      <DialogOverlay class="fixed inset-0 z-[60] bg-carbon-500/60 data-[state=open]:animate-in data-[state=open]:fade-in" />
      <DialogContent
        ref="dialogContentRef"
        tabindex="-1"
        class="
          flex flex-col fixed top-1/2 left-1/2
          -translate-x-1/2 -translate-y-1/2 z-[60]
          max-w-[min(95vw,1200px)] max-h-[90vh] p-m gap-m
          bg-surface-elevated rounded-4xl shadow-2xl overflow-hidden outline-none
        "
        :style="{
          marginBottom: 'env(safe-area-inset-bottom)',
          marginRight: 'env(safe-area-inset-right)',
        }"
        v-bind="hasModalCaption ? {} : { 'aria-describedby': undefined }"
        @open-auto-focus="focusDialogContent"
      >
        <DialogTitle class="sr-only">
          {{ props.caption || props.alt || 'Bilde' }}
        </DialogTitle>

        <div
          ref="scrollRegionRef"
          role="region"
          tabindex="0"
          :aria-label="props.caption || props.alt || 'Bilde'"
          class="flex flex-1 min-h-0 w-full rounded-4xl overflow-auto focus-visible:outline-2 focus-visible:outline-carbon-500"
        >
          <img
            ref="lightboxImgRef"
            :src="props.src"
            :alt="props.alt"
            role="button"
            tabindex="0"
            :aria-label="zoomLabel"
            class="block m-auto rounded-4xl"
            :class="isZoomed
              ? 'min-w-full max-w-none w-auto h-auto cursor-zoom-out'
              : 'max-w-[min(95vw,1200px)] max-h-[calc(90vh-6rem)] w-auto h-auto cursor-zoom-in'"
            @click="toggleZoom"
            @keydown.enter.prevent="toggleZoom"
            @keydown.space.prevent="toggleZoom"
          >
        </div>

        <div class="flex shrink-0 items-center justify-between gap-m pt-m border-t border-border-default dark:border-border-default">
          <DialogDescription v-if="hasModalCaption" as="div" class="flex-1 min-w-0 text-center font-body text-xs leading-5 sm:text-sm">
            <div v-if="props.caption || props.credit" class="flex items-center gap-xs flex-wrap">
              <span v-if="props.caption" class="text-carbon-500 dark:text-carbon-50 not-italic text-left">{{ props.caption }}</span>
              <span v-if="props.credit" class="text-carbon-300 dark:text-carbon-200 not-italic">© {{ props.credit }}</span>
            </div>
            <div v-if="props.byline" class="mt-2xs flex items-center gap-xs">
              <span class="inline-flex items-center justify-center bg-moss-500 text-white text-xs font-medium">
                {{ initial }}
              </span>
              <span>{{ props.byline }}</span>
            </div>
          </DialogDescription>
          <div v-else class="flex-1" />

          <DialogClose
            aria-label="Lukk forhåndsvisning av illustrasjon"
            class="
              flex shrink-0 items-center justify-center
              rounded-lg border border-carbon-500 dark:border-carbon-50 min-w-8 min-h-8
              focus-visible:outline focus-visible:outline-2 focus-visible:outline-carbon-500
            "
          >
            <Icon :icon="faXmark" size="xs" />
          </DialogClose>
        </div>
      </DialogContent>
    </DialogPortal>
  </DialogRoot>
</template>
